import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/ui/DataTable/DataTable';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import { Button } from '../../../components/ui/Button2';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from '../../../components/ui/Empty';
import { TypographyH1, TypographyMuted } from '../../../components/ui/Typography';
import { Icon } from '../../../components/ui/Icon';
import { PageLayout } from '../../../components/layout';
import styles from './DocumentAssessment.module.css';

/**
 * All assessments for the tenant.
 *
 * Readiness is shown alongside open-gap counts rather than instead of them: a
 * high percentage with four high-severity gaps still fails an audit, so the
 * number alone would be misleading.
 */
const STATUS_TONE = {
  completed: 'success',
  running: 'info',
  pending: 'neutral',
  failed: 'error',
};

const AssessmentList = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    import('../api')
      .then((api) => api.listAssessments())
      .then((data) => {
        if (!cancelled) setAssessments(data?.assessments || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load assessments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const columns = [
    { key: 'name', label: 'Assessment', width: '32%',
      render: (row) => row.name || row.assessment_id },
    { key: 'documents', label: 'Docs', width: '10%',
      render: (row) => (row.document_ids || []).length },
    { key: 'status', label: 'Status', width: '14%',
      render: (row) => (
        <StatusPill tone={STATUS_TONE[row.status] || 'neutral'} label={row.status} size="sm" />
      ) },
    { key: 'readiness', label: 'Audit readiness', width: '16%',
      render: (row) => `${row.summary?.readiness_percentage ?? 0}%` },
    { key: 'gaps', label: 'Open gaps', width: '18%',
      render: (row) => (
        <span>
          {row.summary?.open ?? 0}
          {row.summary?.high ? ` (${row.summary.high} high)` : ''}
        </span>
      ) },
    { key: 'created_at', label: 'Created', width: '10%',
      render: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : '—') },
  ];

  return (
    <PageLayout
      activeNavItem="document-assessment"
      currentPage="Document Assessment"
      breadcrumbItems={[{ label: 'Document Assessment' }]}
      contentClassName={styles.contentAreaFullWidth}
      disableScroll
    >
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.headerText}>
          <TypographyH1>Document Assessment</TypographyH1>
          <TypographyMuted className={styles.subtitle}>
            Upload the TARA documents you already produce and see the gaps an
            ISO 21434 assessor would raise.
          </TypographyMuted>
        </div>
        <Button
          onClick={() => navigate('/athena/document-assessment/new')}
          startIcon={<Icon name="add" size="sm" />}
        >
          New assessment
        </Button>
      </header>

      {error ? (
        <Empty role="alert">
          <EmptyHeader>
            <EmptyMedia variant="icon"><Icon name="error" /></EmptyMedia>
            <EmptyTitle>Could not load assessments</EmptyTitle>
            <EmptyDescription>{error}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <DataTable
          columns={columns}
          data={assessments}
          rowKey="assessment_id"
          loading={loading}
          onRowClick={(row) => navigate(`/athena/document-assessment/${row.assessment_id}`)}
          emptyMessage="No assessments yet. Upload your TARA documents to see the gaps."
        />
      )}
    </div>
    </PageLayout>
  );
};

export default AssessmentList;
